import { useParams, useNavigate } from "react-router";
import { useSettlingInPlan } from "@/hooks/useSettlingIn";
import { SettlingInDetail } from "./SettlingInDetail";
import { Loader2 } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { QueryErrorState } from "@/components/ui/query-error-state";

export function SettlingInDetailWrapper() {
  const { t } = useLingui();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: plan, isLoading, error, refetch } = useSettlingInPlan(id || "");

  if (!id) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-destructive">
            {t({ id: "settlingIn.errors.invalidId", message: "Invalid ID" })}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t({ id: "settlingIn.errors.noIdProvided", message: "No plan ID was provided" })}
          </p>
          <button
            onClick={() => navigate("/settling-in")}
            className="mt-4 text-primary hover:underline"
          >
            {t({ id: "settlingIn.navigation.backToList", message: "Back to list" })}
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
        <span className="ml-2 text-foreground">
          {t({ id: "settlingIn.loading.plan", message: "Loading settling-in plan..." })}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <QueryErrorState
            error={error}
            title={t({ id: "settlingIn.errors.loadingError", message: "Failed to load plan" })}
            onRetry={() => void refetch()}
          />
          <button
            onClick={() => navigate("/settling-in")}
            className="mt-4 text-primary hover:underline"
          >
            {t({ id: "settlingIn.navigation.backToList", message: "Back to list" })}
          </button>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold">
            {t({ id: "settlingIn.errors.notFound", message: "Plan not found" })}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t({ id: "settlingIn.errors.notFoundDescription", message: "The settling-in plan could not be found" })}
          </p>
          <button
            onClick={() => navigate("/settling-in")}
            className="mt-4 text-primary hover:underline"
          >
            {t({ id: "settlingIn.navigation.backToList", message: "Back to list" })}
          </button>
        </div>
      </div>
    );
  }

  return <SettlingInDetail plan={plan} planId={id} />;
}

export default SettlingInDetailWrapper;
